import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { type ClaimCampaign, adminEnrollCustomers, adminRemoveCustomers } from '~/services/LuxLoyalty';

import { downloadCsvFile } from '~/utils/csvDownload';

import {
  ADMIN_ENROLLMENT_BATCH_SIZE,
  type AdminEnrollmentMode,
  type AdminEnrollmentProgress,
  type AdminEnrollmentRowOutcome,
  type RunAdminEnrollmentResult,
  countOutcomes,
  getBulkEnrollBlockedReason,
  parseCustomerIds,
  runAdminEnrollmentBatches,
} from './Claims/adminEnrollmentBatches';

interface AdminEnrollmentsDialogProps {
  open: boolean;
  campaign: ClaimCampaign | null;
  onClose: () => void;
}

const OUTCOME_LABELS: Record<AdminEnrollmentRowOutcome, string> = {
  ENROLLED: 'Enrolled',
  ENROLLED_NO_ACCOUNT: 'Enrolled (no Société account yet)',
  ALREADY_ENROLLED: 'Already enrolled',
  ALREADY_FULFILLED: 'Already upgraded',
  REMOVED: 'Removed',
  NOT_ENROLLED: 'Not enrolled',
  ALREADY_REMOVED: 'Already removed',
  FAILED: 'Failed',
  NOT_SENT: 'Not sent',
};

const PROBLEM_OUTCOMES: ReadonlyArray<AdminEnrollmentRowOutcome> = ['FAILED', 'NOT_SENT'];

/** How many invalid values to list before summarising the rest. */
const MAX_INVALID_SHOWN = 5;

/**
 * Bulk admin enrolment / removal for a MATCH campaign.
 *
 * Ids are sent to the API 500 at a time, one batch after another (see runAdminEnrollmentBatches),
 * so the tab has to stay open until the run finishes. Every customer gets a result, downloadable as
 * a CSV; FAILED and NOT_SENT rows can be uploaded again, since resending is safe.
 */
export default function AdminEnrollmentsDialog({ open, campaign, onClose }: AdminEnrollmentsDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [mode, setMode] = useState<AdminEnrollmentMode>('enroll');
  const [idsText, setIdsText] = useState('');
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [progress, setProgress] = useState<AdminEnrollmentProgress | null>(null);
  const [runResult, setRunResult] = useState<RunAdminEnrollmentResult | null>(null);

  const parsed = useMemo(() => parseCustomerIds(idsText), [idsText]);
  const enrollBlockedReason = campaign ? getBulkEnrollBlockedReason(campaign) : null;
  const isBlocked = mode === 'enroll' && enrollBlockedReason !== null;

  // Reset when the dialog opens
  useEffect(() => {
    if (open) {
      setMode('enroll');
      setIdsText('');
      setCsvFileName(null);
      setProgress(null);
      setRunResult(null);
    }
  }, [open]);

  // Leaving the page mid-run stops sending the remaining batches
  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isRunning]);

  const handleCsvFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setCsvFileName(file.name);
    setRunResult(null);
    setIdsText(await file.text());
  };

  const handleStart = useCallback(async () => {
    if (!campaign || parsed.customerIds.length === 0) {
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setIsRunning(true);
    setIsCancelling(false);
    setRunResult(null);
    setProgress({ processed: 0, total: parsed.customerIds.length });

    const send = mode === 'enroll' ? adminEnrollCustomers : adminRemoveCustomers;
    const result = await runAdminEnrollmentBatches(parsed.customerIds, {
      sendBatch: async (customerIds) => (await send(campaign.id, { customerIds })).result.results,
      onProgress: setProgress,
      signal: controller.signal,
    });

    setRunResult(result);
    setIsRunning(false);
    abortRef.current = null;
  }, [campaign, mode, parsed.customerIds]);

  const handleCancel = () => {
    setIsCancelling(true);
    abortRef.current?.abort();
  };

  const handleDownloadResults = () => {
    if (!runResult || !campaign) {
      return;
    }
    downloadCsvFile(
      runResult.results.map((row) => ({ ...row })),
      ['customerId', 'outcome', 'enrollmentId', 'error'],
      `${mode === 'enroll' ? 'enrolment' : 'removal'}-results-${campaign.id}.csv`,
    );
  };

  const outcomeCounts = runResult ? countOutcomes(runResult.results) : null;
  const problemCount = runResult ? runResult.results.filter((row) => PROBLEM_OUTCOMES.includes(row.outcome)).length : 0;
  const batchCount = Math.ceil(parsed.customerIds.length / ADMIN_ENROLLMENT_BATCH_SIZE);
  const customerCount = parsed.customerIds.length;
  const actionLabel = mode === 'enroll' ? 'Enrol' : 'Remove';
  const runningLabel = mode === 'enroll' ? 'Enrolling…' : 'Removing…';

  return (
    <Dialog open={open} onClose={isRunning ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage enrolments — {campaign?.name}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            disabled={isRunning}
            onChange={(_event, value: AdminEnrollmentMode | null) => {
              if (!value) {
                return;
              }
              setMode(value);
              setRunResult(null);
            }}
          >
            <ToggleButton value="enroll">Enrol</ToggleButton>
            <ToggleButton value="remove">Remove</ToggleButton>
          </ToggleButtonGroup>

          {isBlocked ? (
            <Alert severity="error">{enrollBlockedReason}</Alert>
          ) : (
            <Alert severity="info">
              Paste customer ids or choose a CSV with a <strong>customer_id</strong> column. They're sent{' '}
              {ADMIN_ENROLLMENT_BATCH_SIZE} at a time, so keep this tab open until it finishes. Sending the same ids
              again is safe.
              {mode === 'remove' && ' Customers who have already been upgraded are not removed.'}
            </Alert>
          )}

          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              hidden
              onChange={handleCsvFileChange}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              disabled={isRunning || isBlocked}
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                  fileInputRef.current.click();
                }
              }}
            >
              Choose CSV
            </Button>
            {csvFileName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Selected: {csvFileName}{' '}
                <Button
                  size="small"
                  disabled={isRunning}
                  onClick={() => {
                    setCsvFileName(null);
                    setIdsText('');
                    setRunResult(null);
                  }}
                >
                  Clear
                </Button>
              </Typography>
            )}
          </Box>

          <TextField
            label="Customer ids"
            placeholder="One per line, or comma separated. For large lists, choose a CSV instead."
            multiline
            rows={6}
            fullWidth
            value={csvFileName ? '' : idsText}
            disabled={isRunning || isBlocked || !!csvFileName}
            helperText={csvFileName ? 'Using the CSV file. Clear it to paste ids instead.' : undefined}
            onChange={(event) => {
              setIdsText(event.target.value);
              setRunResult(null);
            }}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />

          {customerCount > 0 && !runResult && !isRunning && (
            <Typography variant="body2" color="text.secondary">
              {customerCount.toLocaleString()} customer id{customerCount === 1 ? '' : 's'} ready (
              {batchCount.toLocaleString()} request{batchCount === 1 ? '' : 's'})
              {parsed.duplicateCount > 0 && `, ${parsed.duplicateCount.toLocaleString()} duplicates removed`}.
            </Typography>
          )}

          {parsed.invalidValues.length > 0 && !isRunning && (
            <Alert severity="warning">
              {parsed.invalidValues.length.toLocaleString()} value{parsed.invalidValues.length === 1 ? ' is' : 's are'}{' '}
              not a customer id and will be skipped: {parsed.invalidValues.slice(0, MAX_INVALID_SHOWN).join(', ')}
              {parsed.invalidValues.length > MAX_INVALID_SHOWN && ', …'}
            </Alert>
          )}

          {progress && (isRunning || runResult) && (
            <Box>
              <LinearProgress
                variant="determinate"
                value={progress.total ? (progress.processed / progress.total) * 100 : 0}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {progress.processed.toLocaleString()} of {progress.total.toLocaleString()} processed
                {isCancelling && isRunning && ' — stopping after the current batch…'}
              </Typography>
            </Box>
          )}

          {runResult && outcomeCounts && (
            <Stack spacing={1}>
              {runResult.stopReason && (
                <Alert severity={runResult.stopReason === 'Cancelled' ? 'warning' : 'error'}>
                  Stopped: {runResult.stopReason}
                </Alert>
              )}
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {(Object.keys(outcomeCounts) as Array<AdminEnrollmentRowOutcome>).map((outcome) => (
                  <Chip
                    key={outcome}
                    size="small"
                    color={PROBLEM_OUTCOMES.includes(outcome) ? 'error' : 'default'}
                    label={`${OUTCOME_LABELS[outcome]}: ${outcomeCounts[outcome]?.toLocaleString()}`}
                  />
                ))}
              </Stack>
              {problemCount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Download the results to resend the {problemCount.toLocaleString()} failed or unsent customer
                  {problemCount === 1 ? '' : 's'}.
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {runResult && (
          <Button variant="text" onClick={handleDownloadResults}>
            Download results
          </Button>
        )}
        {isRunning ? (
          <Button variant="text" color="warning" onClick={handleCancel} disabled={isCancelling}>
            Stop
          </Button>
        ) : (
          <Button variant="text" onClick={onClose}>
            {runResult ? 'Close' : 'Cancel'}
          </Button>
        )}
        {!runResult && (
          <Button
            variant="contained"
            color={mode === 'remove' ? 'error' : 'primary'}
            onClick={handleStart}
            disabled={isRunning || isBlocked || customerCount === 0}
          >
            {isRunning
              ? runningLabel
              : customerCount > 0
                ? `${actionLabel} ${customerCount.toLocaleString()}`
                : actionLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
